import {getRankersUserAgent} from "../constants/rankers_user_agent";

export const XHR_ACCEPT_HEADERS = 'text/javascript, text/html, application/xml, text/xml, */*';
export const XHR_REQUESTED_WITH_HEADERS = 'XMLHttpRequest';
export const GET_HTTP_METHOD = 'GET';
export const HEAD_HTTP_METHOD = 'HEAD';

export const POST_HTTP_METHOD = 'POST';

// The single source of truth for the two "identity" headers every Rankers
// front-end request should carry. Centralised here so the logic can't drift
// per-helper (a transposed copy of this in loader.js shipped the wrong values
// for ~14 months — commit cfa8135eb, 2025-04-11).
//
// - X-Requested-With: ensures Rails `request.xhr?` returns true (drives the
//   `format.js` response branch in many controllers).
// - X-Rankers-User-Agent: identifies the client (notably the native app) when
//   the browser User-Agent is unreliable — e.g. webview `fetch` requests that
//   revert their outbound User-Agent to the platform default. Only set when a
//   value is actually available so we never send a literal "null" header on the
//   public website (where getRankersUserAgent() returns null).
//
// Existing values are preserved so callers can override either header.
export function applyRankersRequestHeaders(headers) {
    if (!headers.has('X-Requested-With')) {
        headers.set('X-Requested-With', XHR_REQUESTED_WITH_HEADERS);
    }
    if (!headers.has('X-Rankers-User-Agent')) {
        const userAgent = getRankersUserAgent();
        if (userAgent) headers.set('X-Rankers-User-Agent', userAgent);
    }
    return headers;
}

export function getRequest(url, options={}) {
    options['method'] = options['method'] || GET_HTTP_METHOD;
    options['headers'] = options['headers'] || {};

    // When there are params we need to include them in the request.
    // For GET/HEAD requests they are appended to the URL.
    // Otherwise they are included in the request body.
    const formElement = options['formElement'] || null;
    let params = formElement ? new FormData(formElement) : new FormData();
    const hasParams = (params) ? params.entries().next().value !== undefined : false;
    if (hasParams) {
        if (options['method'] === GET_HTTP_METHOD || options['method'] === HEAD_HTTP_METHOD) {
            for (const pair of params) {
                url.searchParams.append(pair[0], pair[1])
            }
        } else {
            options['body'] = params;
        }
    }

    // Include cookies in the request to approved domains (based on the cookie's `domain` value).
    // The server also needs to respond to the preflight OPTIONS request with the `Access-Control-Allow-Credentials` header set to `true`.
    // See `CrossDomainAccess#set_cross_domain_access` in the Rails app where this is set.
    options['credentials'] ??= 'include';

    let request = new Request(url, options);

    // Note that with CORS the server must allow these headers to be set.
    // See the Rails controller module `CrossDomainAccess#set_cross_domain_access`.
    request.headers.set('Accept', XHR_ACCEPT_HEADERS); // Important! Ensures that the Rails `format.js` response block is run.

    // Set the Rankers identity headers (X-Requested-With + X-Rankers-User-Agent).
    // See applyRankersRequestHeaders above for why these matter, especially for webview requests.
    applyRankersRequestHeaders(request.headers);

    // Override the headers set above with any headers passed in the options.
    Object.entries(options['headers']).forEach(([key, value]) => {
        request.headers.set(key, value);
    });

    return request;
}
